import { useCallback, useRef, useEffect } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { useTaskStore } from '../stores/taskStore';
import { AppNode, NodeType, NodeStatus, PanoramaQuality, PanoramaQualityDimensions } from '../types';
import { generateApi, GenerateRequest } from '../services/generateApi';
import { isBackendAssetUrl, saveSingleAsset } from '../utils/assetUtils';
import { toast } from 'sonner';

export function usePanoramaGenerator(
    saveHistory: () => void,
    handleAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string) => void,
) {
    const nodes = useNodeStore((s) => s.nodes);
    const addNode = useNodeStore((s) => s.addNode);
    const addConnection = useNodeStore((s) => s.addConnection);
    const updateNode = useNodeStore((s) => s.updateNode);
    const currentProjectId = useProjectStore((s) => s.currentProjectId);
    const addTaskWithRequest = useTaskStore((s) => s.addTaskWithRequest);
    const removeTask = useTaskStore((s) => s.removeTask);
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    const handleGeneratePanorama = useCallback(async (sourceNodeId: string, params: {
        prompt: string;
        quality: PanoramaQuality;
        referenceImageUrl?: string;
        mode: 'txt2panorama' | 'img2panorama';
        model?: string;
    }) => {
        const sourceNode = nodesRef.current.find(n => n.id === sourceNodeId);
        if (!sourceNode) return;

        const newNode: AppNode = {
            id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            type: NodeType.PANORAMA_GENERATOR,
            x: sourceNode.x + 500,
            y: sourceNode.y,
            width: 420,
            title: '720°全景图',
            status: NodeStatus.WORKING,
            data: {
                prompt: params.prompt,
                model: params.model || 'gemini-2.5-flash-image',
                aspectRatio: '2:1',
                panoramaMode: params.mode,
                panoramaQuality: params.quality,
                image: params.referenceImageUrl,
            },
            inputs: [sourceNodeId],
        };

        try {
            saveHistory();
            addNode(newNode);
            addConnection(sourceNodeId, newNode.id);

            const request: GenerateRequest = {
                modelId: params.model || 'gemini-2.5-flash-image',
                prompt: params.prompt || '生成一张720°全景图',
                mode: params.mode,
                inputImages: params.referenceImageUrl ? [params.referenceImageUrl] : undefined,
                params: {
                    quality: params.quality,
                    aspectRatio: '2:1',
                },
            };

            const asyncResult = await generateApi.generateAsync(request);
            const taskId = asyncResult.taskId;

            addTaskWithRequest(taskId, newNode.id, NodeType.PANORAMA_GENERATOR, request as Record<string, any>);
            updateNode(newNode.id, { taskId });

            const statusResult = await generateApi.pollTaskUntilComplete(taskId);

            if (statusResult.status === 'failed') {
                throw new Error(statusResult.error || '全景图生成失败');
            }

            const resultData = statusResult.result;
            if (!resultData?.results || resultData.results.length === 0) {
                throw new Error('未收到生成的全景图');
            }

            let finalUrl: string = resultData.results[0]?.url || '';
            if (!finalUrl) throw new Error('未收到生成的全景图');
            if (currentProjectId && !isBackendAssetUrl(finalUrl)) {
                finalUrl = await saveSingleAsset(currentProjectId, finalUrl, 'panorama.png', {
                    generator_node_id: newNode.id,
                    generator_type: 'panorama',
                    prompt: params.prompt,
                    model: params.model || '',
                });
            }

            updateNode(newNode.id, {
                image: finalUrl,
                panoramaOutputUrl: finalUrl,
                panoramaOutputDimensions: PanoramaQualityDimensions[params.quality],
                isPanoramaPreviewOpen: true,
            }, undefined, undefined, NodeStatus.SUCCESS);

            removeTask(taskId);
            handleAssetGenerated('image', finalUrl, params.prompt || '720° Panorama');
            toast.success('全景图生成成功');
        } catch (e: any) {
            console.error('全景图生成失败:', e);
            const pendingTask = useTaskStore.getState().getTaskByNodeId(newNode.id);
            if (pendingTask) removeTask(pendingTask.taskId);
            updateNode(newNode.id, { error: e.message }, undefined, undefined, NodeStatus.ERROR);
            toast.error('全景图生成失败', { description: e.message, duration: 6000 });
        }
    }, [addNode, addConnection, updateNode, handleAssetGenerated, currentProjectId, saveHistory, addTaskWithRequest, removeTask]);

    return { handleGeneratePanorama };
}
